import React, { useEffect } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_BRAND = {
  primary: "#002FA7",
  accent: "#FFC107",
  neutral_dark: "#52525B",
  neutral_light: "#F4F4F5",
};

const DEFAULT_FONTS = {
  family: "",
  heading_weight: 800,
  subheading_weight: 700,
  body_weight: 500,
  small_weight: 400,
};

// Common condensed / bold web-safe stacks the admin can pick in one click.
const FONT_PRESETS = [
  {
    label: "Helvetica Neue Condensed (GBE Group style)",
    family: "\"Helvetica Neue Condensed\", \"Helvetica Neue\", \"HelveticaNeue-CondensedBold\", Impact, Helvetica, Arial, sans-serif",
    heading_weight: 900,    // 87 Heavy Condensed
    subheading_weight: 700, // 77 Bold Condensed
    body_weight: 500,       // 67 Medium Condensed
    small_weight: 400,      // 57 Condensed
  },
  {
    label: "Inter (modern UI)",
    family: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    heading_weight: 800,
    subheading_weight: 700,
    body_weight: 500,
    small_weight: 400,
  },
  {
    label: "Default — Cabinet Grotesk + IBM Plex (reset)",
    family: "",
    heading_weight: 800,
    subheading_weight: 700,
    body_weight: 500,
    small_weight: 400,
  },
];

const WEIGHT_OPTIONS = [
  { v: 300, l: "300 — Light" },
  { v: 400, l: "400 — Regular / 57 Condensed" },
  { v: 500, l: "500 — Medium / 67 Medium Condensed" },
  { v: 600, l: "600 — Semibold" },
  { v: 700, l: "700 — Bold / 77 Bold Condensed" },
  { v: 800, l: "800 — Extrabold" },
  { v: 900, l: "900 — Heavy / 87 Heavy Condensed" },
];

export default function BrandSettingsEditor({ clientId }) {
  const [colors, setColors] = React.useState(DEFAULT_BRAND);
  const [fonts, setFonts] = React.useState(DEFAULT_FONTS);
  const [busy, setBusy] = React.useState(false);
  const [fontsBusy, setFontsBusy] = React.useState(false);

  useEffect(() => {
    api.get(`/clients/${clientId}`).then(({ data }) => {
      if (data.brand_colors) setColors({ ...DEFAULT_BRAND, ...data.brand_colors });
      if (data.brand_fonts) setFonts({ ...DEFAULT_FONTS, ...data.brand_fonts });
    });
  }, [clientId]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/clients/${clientId}/brand-colors`, colors);
      toast.success("Brand colours saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const saveFonts = async () => {
    setFontsBusy(true);
    try {
      await api.patch(`/clients/${clientId}/brand-fonts`, fonts);
      toast.success("Brand fonts saved — applies on the next report view");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setFontsBusy(false); }
  };

  const applyPreset = (preset) => {
    setFonts({
      family: preset.family,
      heading_weight: preset.heading_weight,
      subheading_weight: preset.subheading_weight,
      body_weight: preset.body_weight,
      small_weight: preset.small_weight,
    });
  };

  const FIELDS = [
    { key: "primary", label: "Primary", desc: "Section headings, table headers" },
    { key: "accent", label: "Accent", desc: "Highlights, accent stripe under cover title" },
    { key: "neutral_dark", label: "Mid grey", desc: "Body label text colour" },
    { key: "neutral_light", label: "Light grey", desc: "Alt row background" },
  ];

  const previewFamily = fonts.family || "var(--font-display, 'Cabinet Grotesk'), sans-serif";

  return (
    <Card className="rounded-sm border-zinc-200 lg:col-span-2" data-testid="brand-colors-card">
      <CardContent className="p-6 space-y-8">
        {/* ===== COLOURS — stacked vertically ===== */}
        <section>
          <div className="mb-4">
            <div className="label-overline">Branding</div>
            <h2 className="font-display font-bold text-xl tracking-tight">Report colour palette</h2>
            <p className="text-sm text-zinc-500 mt-1">
              These colours are applied to the monthly PDF report and the web report view. Drop in the
              client&apos;s brand hex codes so deliverables match their identity.
            </p>
          </div>

          <div className="flex flex-col gap-3" data-testid="brand-colors-stack">
            {FIELDS.map((f) => (
              <div
                key={f.key}
                className="flex items-center gap-4 p-3 rounded-sm border border-zinc-200 bg-white hover:border-zinc-300 transition-colors"
                data-testid={`brand-${f.key}-card`}
              >
                <input
                  type="color"
                  value={colors[f.key]}
                  onChange={(e) => setColors({ ...colors, [f.key]: e.target.value })}
                  className="h-12 w-14 border border-zinc-200 rounded-sm cursor-pointer p-0.5 shrink-0"
                  data-testid={`brand-${f.key}-picker`}
                />
                <div className="flex-1 min-w-0">
                  <Label className="label-overline">{f.label}</Label>
                  <p className="text-[11px] text-zinc-500 mt-0.5">{f.desc}</p>
                </div>
                <Input
                  value={colors[f.key]}
                  onChange={(e) => setColors({ ...colors, [f.key]: e.target.value })}
                  className="rounded-sm font-mono text-xs h-10 w-32 shrink-0"
                  data-testid={`brand-${f.key}-input`}
                />
              </div>
            ))}
          </div>

          <div className="pt-4 mt-4 border-t border-zinc-100">
            <Button onClick={save} disabled={busy}
              className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
              data-testid="brand-save-button">
              <Save className="h-3.5 w-3.5 mr-1.5" /> Save palette
            </Button>
          </div>
        </section>

        {/* ===== FONTS ===== */}
        <section data-testid="brand-fonts-section" className="pt-2">
          <div className="mb-4">
            <div className="label-overline">Branding · typography</div>
            <h2 className="font-display font-bold text-xl tracking-tight">Report fonts</h2>
            <p className="text-sm text-zinc-500 mt-1">
              Pick the font family + weight roles used by the client&apos;s own brand
              guidelines. Applied to the web report view; the PDF will use the
              closest matching weight that ReportLab can embed. Leave the family
              blank to fall back to the default stack.
            </p>
          </div>

          {/* Presets */}
          <div className="flex flex-wrap gap-2 mb-4">
            {FONT_PRESETS.map((p) => (
              <Button
                key={p.label}
                type="button" variant="outline" size="sm"
                className="rounded-sm text-xs h-8"
                onClick={() => applyPreset(p)}
                data-testid={`brand-font-preset-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}`}
              >
                {p.label}
              </Button>
            ))}
          </div>

          {/* Family input */}
          <div className="space-y-2 mb-4">
            <Label className="label-overline">Font family (CSS stack)</Label>
            <Input
              value={fonts.family}
              onChange={(e) => setFonts({ ...fonts, family: e.target.value })}
              placeholder='e.g. "Helvetica Neue Condensed", "Helvetica Neue", Arial, sans-serif'
              className="rounded-sm font-mono text-xs h-10"
              data-testid="brand-font-family-input"
            />
            <p className="text-[11px] text-zinc-500">
              A standard CSS font-family stack. Leave blank to use the default brand stack.
            </p>
          </div>

          {/* Weights — stacked vertically (4 roles) */}
          <div className="flex flex-col gap-2.5" data-testid="brand-fonts-stack">
            {[
              { key: "heading_weight", label: "Heading", desc: "Cover title, page H1 / H2 (e.g. 87 Heavy Condensed)" },
              { key: "subheading_weight", label: "Subheading", desc: "Section subheads, table headers (e.g. 77 Bold Condensed)" },
              { key: "body_weight", label: "Body", desc: "Main paragraph text (e.g. 67 Medium Condensed)" },
              { key: "small_weight", label: "Smaller text", desc: "Captions, footnotes, table cells (e.g. 57 Condensed)" },
            ].map((row) => (
              <div
                key={row.key}
                className="flex items-center gap-4 p-3 rounded-sm border border-zinc-200 bg-white"
                data-testid={`brand-font-${row.key}-row`}
              >
                <div
                  className="h-12 w-24 shrink-0 grid place-items-center rounded-sm bg-zinc-50 border border-zinc-200 text-zinc-900"
                  style={{
                    fontFamily: previewFamily,
                    fontWeight: fonts[row.key],
                    fontSize: "1.05rem",
                    letterSpacing: "-0.01em",
                  }}
                  data-testid={`brand-font-${row.key}-preview`}
                  title="Preview"
                >
                  Aa
                </div>
                <div className="flex-1 min-w-0">
                  <Label className="label-overline">{row.label}</Label>
                  <p className="text-[11px] text-zinc-500 mt-0.5">{row.desc}</p>
                </div>
                <select
                  value={fonts[row.key]}
                  onChange={(e) => setFonts({ ...fonts, [row.key]: parseInt(e.target.value, 10) })}
                  className="rounded-sm border border-zinc-200 h-10 px-2 text-xs font-mono bg-white shrink-0 w-[260px]"
                  data-testid={`brand-font-${row.key}-select`}
                >
                  {WEIGHT_OPTIONS.map((w) => (
                    <option key={w.v} value={w.v}>{w.l}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          {/* Live preview block */}
          <div
            className="mt-5 p-5 rounded-sm border border-zinc-200 bg-white"
            data-testid="brand-fonts-preview"
            style={{ fontFamily: previewFamily }}
          >
            <div className="label-overline">Live preview</div>
            <div
              style={{ fontWeight: fonts.heading_weight, fontSize: "2.25rem", lineHeight: 1.05, letterSpacing: "-0.02em" }}
              className="mt-2"
            >
              Monthly Marketing Report
            </div>
            <div
              style={{ fontWeight: fonts.subheading_weight, fontSize: "1.05rem", color: colors.primary }}
              className="mt-3 uppercase tracking-wider"
            >
              Executive Summary
            </div>
            <p
              style={{ fontWeight: fonts.body_weight, fontSize: "0.95rem", lineHeight: 1.5 }}
              className="mt-2 text-zinc-700"
            >
              This is how the body copy will appear in the rendered web report. Use this
              preview to validate readability before saving — the chosen weight is the
              one applied to paragraph text below each chart.
            </p>
            <p
              style={{ fontWeight: fonts.small_weight, fontSize: "0.78rem" }}
              className="mt-2 text-zinc-500"
            >
              Smaller text · used for table cells, captions and footnotes throughout the report.
            </p>
          </div>

          <div className="pt-4 mt-4 border-t border-zinc-100">
            <Button onClick={saveFonts} disabled={fontsBusy}
              className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
              data-testid="brand-fonts-save-button">
              <Save className="h-3.5 w-3.5 mr-1.5" /> Save fonts
            </Button>
          </div>
        </section>
      </CardContent>
    </Card>
  );
}
